import React from 'react';
import styled from 'styled-components';
import {
  Zap,
  FileText,
  MousePointer,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Settings,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../../../styles/theme';

function getUnmappedVariables(state, template) {
  if (!template || !template.variables || !template.variables.length) return [];
  const unmapped = [];
  template.variables.forEach((v, idx) => {
    const key = String(idx + 1);
    const mapped = state.variableMapping?.[key] || state.variables?.[key];
    if (!mapped) {
      unmapped.push(v.name || `{{${key}}}`);
    }
  });
  return unmapped;
}

function getButtonsWithoutTool(state) {
  const mappings = state.buttonMappings || {};
  const incomplete = [];
  Object.entries(mappings).forEach(([buttonId, mapping]) => {
    if (!mapping.toolName) {
      incomplete.push(buttonId);
    }
  });
  return incomplete;
}

export default function StepSummary({ state, errors, agents, connectors, mktTemplates, onNavigateToStep }) {
  const { t } = useTranslation('campaigns');

  const TRIGGER_LABELS = {
    manual: t('summary.triggerManual'),
    scheduled: t('summary.triggerScheduled'),
    endpoint: t('summary.triggerEndpoint'),
    email: t('summary.triggerEmail'),
  };

  const CHANNEL_LABELS = {
    whatsapp: 'WhatsApp',
    email: t('summary.channelEmail'),
  };

  const agent = agents?.find((a) => a.instanceId === state.instanceId);
  const template = mktTemplates?.find((t) => t.templateId === state.templateId || t.id === state.templateId);

  const triggerType = state.triggerConfig?.type || 'manual';
  const unmappedVars = getUnmappedVariables(state, template);
  const buttonsWithoutTool = getButtonsWithoutTool(state);

  const templateVars = template?.variables || [];
  const totalVars = templateVars.length;
  const mappedCount = totalVars - unmappedVars.length;

  const buttonMappings = state.buttonMappings || {};
  const totalButtons = Object.keys(buttonMappings).length;
  const mappedButtons = Object.values(buttonMappings).filter((m) => m.toolName).length;

  return (
    <Container>
      {/* Section 1: Básico → step 0 */}
      <SectionCard
        onClick={() => onNavigateToStep(0)}
        role="button"
        tabIndex={0}
        aria-label={t('summary.editBasic')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToStep(0); }}
      >
        <SectionHeader>
          <SectionIcon $color={theme.colors.primary}>
            <Settings size={18} />
          </SectionIcon>
          <SectionTitle>{t('summary.basicTitle')}</SectionTitle>
          <StatusIcon $complete={!!(state.name && state.instanceId && state.channel)}>
            {state.name && state.instanceId && state.channel
              ? <CheckCircle size={16} />
              : <AlertTriangle size={16} />
            }
          </StatusIcon>
        </SectionHeader>
        <SectionBody>
          <SummaryRow>
            <RowLabel>{t('summary.name')}</RowLabel>
            <RowValue>{state.name || <MissingText>{t('summary.notDefined')}</MissingText>}</RowValue>
          </SummaryRow>
          <SummaryRow>
            <RowLabel>{t('summary.agent')}</RowLabel>
            <RowValue>{agent?.name || <MissingText>{t('summary.notSelected')}</MissingText>}</RowValue>
          </SummaryRow>
          <SummaryRow>
            <RowLabel>{t('summary.channel')}</RowLabel>
            <RowValue>
              {state.channel
                ? <Badge $variant="channel">{CHANNEL_LABELS[state.channel] || state.channel}</Badge>
                : <MissingText>{t('summary.notSelected')}</MissingText>
              }
            </RowValue>
          </SummaryRow>
        </SectionBody>
      </SectionCard>

      {/* Section 2: Trigger → step 1 */}
      <SectionCard
        onClick={() => onNavigateToStep(1)}
        role="button"
        tabIndex={0}
        aria-label={t('summary.editTrigger')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToStep(1); }}
      >
        <SectionHeader>
          <SectionIcon $color={theme.colors.warning}>
            <Zap size={18} />
          </SectionIcon>
          <SectionTitle>{t('summary.triggerTitle')}</SectionTitle>
        </SectionHeader>
        <SectionBody>
          <SummaryRow>
            <RowLabel>{t('summary.type')}</RowLabel>
            <RowValue>
              <Badge $variant="trigger">{TRIGGER_LABELS[triggerType] || triggerType}</Badge>
            </RowValue>
          </SummaryRow>
          {triggerType === 'endpoint' && (
            <>
              <SummaryRow>
                <RowLabel>{t('summary.verifyToken')}</RowLabel>
                <RowValue>
                  {state.triggerConfig?.verifyToken
                    ? <MaskedToken>{state.triggerConfig.verifyToken.slice(0, 4)}••••</MaskedToken>
                    : <MissingText>{t('summary.notGenerated')}</MissingText>
                  }
                </RowValue>
              </SummaryRow>
              <SummaryRow>
                <RowLabel>{t('summary.endpoint')}</RowLabel>
                <RowValue>
                  <CodeText>POST /api/campaigns/trigger/{'{'}id{'}'}</CodeText>
                </RowValue>
              </SummaryRow>
            </>
          )}
          {(triggerType === 'manual' || triggerType === 'scheduled') && (
            <>
              <SummaryRow>
                <RowLabel>{t('summary.connector')}</RowLabel>
                <RowValue>
                  {state.dataSource?.connectorId
                    ? (connectors?.find((c) => c.connectorId === state.dataSource.connectorId)?.name || state.dataSource.connectorId)
                    : <MissingText>{t('summary.notSelected')}</MissingText>
                  }
                </RowValue>
              </SummaryRow>
              <SummaryRow>
                <RowLabel>{t('summary.function')}</RowLabel>
                <RowValue>
                  {state.dataSource?.toolName || <MissingText>{t('summary.notSelectedFem')}</MissingText>}
                </RowValue>
              </SummaryRow>
            </>
          )}
        </SectionBody>
      </SectionCard>

      {/* Section 3: Template → step 2 */}
      <SectionCard
        onClick={() => onNavigateToStep(2)}
        role="button"
        tabIndex={0}
        aria-label={t('summary.editTemplate')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToStep(2); }}
      >
        <SectionHeader>
          <SectionIcon $color={theme.colors.info}>
            <FileText size={18} />
          </SectionIcon>
          <SectionTitle>{t('summary.templateTitle')}</SectionTitle>
          {unmappedVars.length > 0 && (
            <WarningBadge>
              <AlertTriangle size={14} />
            </WarningBadge>
          )}
        </SectionHeader>
        <SectionBody>
          <SummaryRow>
            <RowLabel>{t('summary.template')}</RowLabel>
            <RowValue>{template?.name || <MissingText>{t('summary.notSelected')}</MissingText>}</RowValue>
          </SummaryRow>
          {totalVars > 0 && (
            <SummaryRow>
              <RowLabel>{t('summary.variables')}</RowLabel>
              <RowValue>
                <Badge $variant={mappedCount === totalVars ? 'success' : 'warning'}>
                  {t('summary.variablesMapped', { mapped: mappedCount, total: totalVars })}
                </Badge>
              </RowValue>
            </SummaryRow>
          )}
          {unmappedVars.length > 0 && (
            <WarningMessage>
              <AlertTriangle size={14} />
              <span>{t('summary.unmappedVariables', { vars: unmappedVars.join(', ') })}</span>
            </WarningMessage>
          )}
        </SectionBody>
      </SectionCard>

      {/* Section 4: Botões → step 3 */}
      <SectionCard
        onClick={() => onNavigateToStep(3)}
        role="button"
        tabIndex={0}
        aria-label={t('summary.editButtons')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToStep(3); }}
      >
        <SectionHeader>
          <SectionIcon $color={theme.colors.secondary}>
            <MousePointer size={18} />
          </SectionIcon>
          <SectionTitle>{t('summary.buttonsTitle')}</SectionTitle>
          {buttonsWithoutTool.length > 0 && (
            <WarningBadge>
              <AlertTriangle size={14} />
            </WarningBadge>
          )}
        </SectionHeader>
        <SectionBody>
          {totalButtons > 0 ? (
            <>
              <SummaryRow>
                <RowLabel>{t('summary.configuredButtons')}</RowLabel>
                <RowValue>
                  <Badge $variant={mappedButtons === totalButtons ? 'success' : 'warning'}>
                    {t('summary.buttonsMapped', { mapped: mappedButtons, total: totalButtons })}
                  </Badge>
                </RowValue>
              </SummaryRow>
              {Object.entries(buttonMappings).map(([buttonId, mapping]) => (
                <ButtonFlowRow key={buttonId}>
                  <FlowItem>{mapping.buttonText || buttonId}</FlowItem>
                  <FlowArrow>→</FlowArrow>
                  <FlowItem>{mapping.toolName || <MissingText>{t('summary.noTool')}</MissingText>}</FlowItem>
                  <FlowArrow>→</FlowArrow>
                  <FlowItem $truncate>
                    {mapping.feedbackMessage || <MissingText>{t('summary.noMessage')}</MissingText>}
                  </FlowItem>
                </ButtonFlowRow>
              ))}
              {buttonsWithoutTool.length > 0 && (
                <WarningMessage>
                  <AlertTriangle size={14} />
                  <span>{t('summary.buttonsWithoutTool', { count: buttonsWithoutTool.length })}</span>
                </WarningMessage>
              )}
            </>
          ) : (
            <EmptyText>{t('summary.noButtons')}</EmptyText>
          )}
        </SectionBody>
      </SectionCard>

      {/* Section 5: Dados/Schedule → step 1 */}
      <SectionCard
        onClick={() => onNavigateToStep(1)}
        role="button"
        tabIndex={0}
        aria-label={t('summary.editSchedule')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onNavigateToStep(1); }}
      >
        <SectionHeader>
          <SectionIcon $color={theme.colors.success}>
            <Calendar size={18} />
          </SectionIcon>
          <SectionTitle>{t('summary.scheduleTitle')}</SectionTitle>
        </SectionHeader>
        <SectionBody>
          {triggerType === 'scheduled' && (
            <>
              {state.schedule?.cronExpression && (
                <SummaryRow>
                  <RowLabel>{t('summary.cron')}</RowLabel>
                  <RowValue><CodeText>{state.schedule.cronExpression}</CodeText></RowValue>
                </SummaryRow>
              )}
              {state.schedule?.scheduledAt && (
                <SummaryRow>
                  <RowLabel>{t('summary.scheduledFor')}</RowLabel>
                  <RowValue>{state.schedule.scheduledAt}</RowValue>
                </SummaryRow>
              )}
            </>
          )}
          {triggerType === 'manual' && (
            <SummaryRow>
              <RowLabel>{t('summary.mode')}</RowLabel>
              <RowValue><Badge $variant="neutral">{t('summary.modeManual')}</Badge></RowValue>
            </SummaryRow>
          )}
          {triggerType === 'endpoint' && (
            <SummaryRow>
              <RowLabel>{t('summary.mode')}</RowLabel>
              <RowValue><Badge $variant="neutral">{t('summary.modeWebhook')}</Badge></RowValue>
            </SummaryRow>
          )}
          {triggerType === 'email' && (
            <SummaryRow>
              <RowLabel>{t('summary.mode')}</RowLabel>
              <RowValue><Badge $variant="neutral">{t('summary.modeEmail')}</Badge></RowValue>
            </SummaryRow>
          )}
          {state.rateLimit && (state.rateLimit.batchSize !== 30 || state.rateLimit.delayMs !== 0) && (
            <SummaryRow>
              <RowLabel>{t('summary.rateLimit')}</RowLabel>
              <RowValue>
                {t('summary.msgsPerBatch', { count: state.rateLimit.batchSize })}
                {state.rateLimit.delayMs > 0 && t('summary.delaySuffix', { ms: state.rateLimit.delayMs })}
              </RowValue>
            </SummaryRow>
          )}
        </SectionBody>
      </SectionCard>
    </Container>
  );
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionCard = styled.div`
  padding: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.white};
  cursor: pointer;
  transition: ${theme.transitions.fast};

  &:hover {
    border-color: ${theme.colors.primaryBorder};
    box-shadow: ${theme.shadows.sm};
    background: ${theme.colors.gray50};
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const SectionIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${theme.radii.md};
  background: ${(props) => props.$color ? `${props.$color}15` : theme.colors.bgSecondary};
  color: ${(props) => props.$color || theme.colors.textSecondary};
  flex-shrink: 0;
`;

const SectionTitle = styled.h4`
  flex: 1;
  margin: 0;
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) => (props.$complete ? theme.colors.success : theme.colors.warning)};
`;

const WarningBadge = styled.div`
  display: flex;
  align-items: center;
  color: ${theme.colors.warning};
`;

const SectionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  padding-left: calc(32px + ${theme.spacing[2]});
`;

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm};
`;

const RowLabel = styled.span`
  color: ${theme.colors.textMuted};
  min-width: 100px;
  flex-shrink: 0;
`;

const RowValue = styled.span`
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.medium};
`;

const MissingText = styled.span`
  color: ${theme.colors.textMuted};
  font-style: italic;
  font-weight: ${theme.typography.normal};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${theme.spacing[2]};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.medium};

  ${(props) => {
    switch (props.$variant) {
      case 'success':
        return `
          background: ${theme.colors.successLight};
          color: ${theme.colors.success};
          border: 1px solid ${theme.colors.successBorder};
        `;
      case 'warning':
        return `
          background: ${theme.colors.warningLight};
          color: ${theme.colors.warning};
          border: 1px solid ${theme.colors.warningBorder};
        `;
      case 'trigger':
        return `
          background: ${theme.colors.warningLight};
          color: ${theme.colors.gray800};
          border: 1px solid ${theme.colors.warningBorder};
        `;
      case 'channel':
        return `
          background: ${theme.colors.successLight};
          color: ${theme.colors.gray800};
          border: 1px solid ${theme.colors.successBorder};
        `;
      case 'neutral':
      default:
        return `
          background: ${theme.colors.bgSecondary};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.border};
        `;
    }
  }}
`;

const WarningMessage = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.warningLight};
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.warning};

  span {
    color: ${theme.colors.textSecondary};
  }
`;

const EmptyText = styled.span`
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
  font-style: italic;
`;

const MaskedToken = styled.code`
  font-family: ${theme.typography.fontMono};
  font-size: ${theme.typography.xs};
  padding: 2px ${theme.spacing[1]};
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.radii.sm};
`;

const CodeText = styled.code`
  font-family: ${theme.typography.fontMono};
  font-size: ${theme.typography.xs};
  padding: 2px ${theme.spacing[1]};
  background: ${theme.colors.bgSecondary};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.textPrimary};
`;

const ButtonFlowRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  font-size: ${theme.typography.xs};
  padding: ${theme.spacing[1]} 0;
`;

const FlowItem = styled.span`
  color: ${theme.colors.textPrimary};
  font-weight: ${theme.typography.medium};
  ${(props) => props.$truncate && `
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  `}
`;

const FlowArrow = styled.span`
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.xs};
`;
